/**
 * Monitor 2.0 config model — pure, tested logic for the home monitor
 * page: per-probe layout config (visible/order/size/target), reef
 * target presets, target status math, and the tolerant normalize/merge
 * that reconciles the stored config with the live Apex probe catalog.
 *
 * Persisted via `useServerBackedState('monitor-config')` — the blob is
 * versionless and every reader runs it through
 * `sanitizeMonitorConfig` + `mergeConfigWithProbes`, so unknown fields
 * are dropped and missing ones get defaults (rule 7: old data keeps
 * rendering).
 */

export interface ProbeTarget {
  readonly min: number;
  readonly max: number;
}

export type ProbeSize = 'md' | 'lg';

export interface MonitorProbeConfig {
  /** Raw Apex probe name — doubles as the timeseries `sensorId`. */
  readonly id: string;
  readonly visible: boolean;
  readonly order: number;
  readonly size: ProbeSize;
  readonly target?: ProbeTarget;
}

export interface ComparePair {
  readonly a: string;
  readonly b: string;
}

export interface MonitorConfig {
  readonly probes: readonly MonitorProbeConfig[];
  readonly rangeHours: number;
  readonly compare?: ComparePair | null;
}

/** Minimal shape of a live Apex probe needed for merging + presets. */
export interface LiveProbeInfo {
  readonly name: string;
  readonly probeType: string;
}

// ── Range options ────────────────────────────────────────────────────

export interface RangeOption {
  readonly hours: number;
  readonly label: string;
}

export const RANGE_OPTIONS: readonly RangeOption[] = [
  { hours: 1, label: '1H' },
  { hours: 6, label: '6H' },
  { hours: 24, label: '24H' },
  { hours: 72, label: '3D' },
  { hours: 168, label: '7D' },
  { hours: 720, label: '30D' },
];

export const DEFAULT_RANGE_HOURS = 24;

/**
 * Pre-2.0 monitor stored its range in localStorage under this key
 * ('6h' | '24h' | '7d' | '30d' | '90d'). Read it once so the very
 * first Monitor 2.0 config default keeps the operator's old choice.
 * Harmless when absent (returns null). 90d maps to 30d — the new
 * picker tops out there.
 */
const LEGACY_RANGE_KEY = 'reefapp:monitor-range';

const LEGACY_RANGE_HOURS: Readonly<Record<string, number>> = {
  '6h': 6,
  '24h': 24,
  '7d': 168,
  '30d': 720,
  '90d': 720,
};

export function readLegacyRangeHours(): number | null {
  try {
    if (typeof window === 'undefined') return null;
    const raw = window.localStorage.getItem(LEGACY_RANGE_KEY);
    if (raw === null) return null;
    return LEGACY_RANGE_HOURS[raw] ?? null;
  } catch {
    return null;
  }
}

// ── Reef target presets ──────────────────────────────────────────────

export interface TargetPreset {
  /** Human label shown on the "preset" button, e.g. "76–80". */
  readonly label: string;
  /** Case-insensitive substrings matched against probe name + type. */
  readonly patterns: readonly string[];
  readonly target: ProbeTarget;
}

/**
 * Default reef targets matched by case-insensitive *contains* against
 * the probe name and probeType. ORDER MATTERS — specific patterns come
 * before loose ones ('po4'/'phos' before 'ph', 'salt'/'cond' before
 * 'ca') so e.g. a "Phosphate" probe never lands on the pH preset.
 */
export const TARGET_PRESETS: readonly TargetPreset[] = [
  { label: '76–80 °F', patterns: ['tmp', 'temp'], target: { min: 76, max: 80 } },
  { label: '0.02–0.15', patterns: ['po4', 'phos'], target: { min: 0.02, max: 0.15 } },
  { label: '1–15', patterns: ['no3', 'nitrat'], target: { min: 1, max: 15 } },
  { label: '7–10 dKH', patterns: ['alk', 'kh'], target: { min: 7, max: 10 } },
  { label: '33–36', patterns: ['salt', 'salin', 'cond'], target: { min: 33, max: 36 } },
  { label: '300–450 mV', patterns: ['orp'], target: { min: 300, max: 450 } },
  { label: '1250–1400', patterns: ['mg', 'mag'], target: { min: 1250, max: 1400 } },
  { label: '380–460', patterns: ['ca', 'cal'], target: { min: 380, max: 460 } },
  { label: '7.9–8.4', patterns: ['ph'], target: { min: 7.9, max: 8.4 } },
];

/**
 * Find the default target preset for a probe, or `null` when nothing
 * matches (the card then renders without a target band).
 */
export function findPreset(name: string, probeType?: string): TargetPreset | null {
  const haystack = `${name} ${probeType ?? ''}`.toLowerCase();
  for (const preset of TARGET_PRESETS) {
    if (preset.patterns.some((p) => haystack.includes(p))) return preset;
  }
  return null;
}

// ── Status math ──────────────────────────────────────────────────────

export type TargetStatus = 'none' | 'good' | 'warn' | 'bad';

/** Fraction of the target span treated as the amber "close to an edge" band. */
export const WARN_EDGE_FRACTION = 0.05;

/**
 * Classify a live value against a target range.
 *
 *   - no target        → 'none'  (neutral display)
 *   - outside [min,max] → 'bad'
 *   - inside but within 5% of the span from an edge → 'warn'
 *   - otherwise        → 'good'
 *
 * Non-finite values with a target are 'bad' so a broken probe read is
 * never silently ignored. Bounds are inclusive: a value exactly on
 * min/max is *inside* the target, hence 'warn' (it sits on the edge).
 */
export function statusOf(value: number, target?: ProbeTarget | null): TargetStatus {
  if (target === undefined || target === null) return 'none';
  if (!Number.isFinite(value)) return 'bad';
  if (value < target.min || value > target.max) return 'bad';
  const margin = (target.max - target.min) * WARN_EDGE_FRACTION;
  if (value <= target.min + margin || value >= target.max - margin) return 'warn';
  return 'good';
}

// ── Tolerant parsing (unknown → MonitorConfig) ───────────────────────

function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

function parseTarget(v: unknown): ProbeTarget | null {
  if (!isRecord(v)) return null;
  const min = v['min'];
  const max = v['max'];
  if (typeof min !== 'number' || typeof max !== 'number') return null;
  if (!Number.isFinite(min) || !Number.isFinite(max) || min > max) return null;
  return { min, max };
}

function parseProbeEntry(v: unknown, fallbackOrder: number): MonitorProbeConfig | null {
  if (!isRecord(v)) return null;
  const id = v['id'];
  if (typeof id !== 'string' || id.length === 0) return null;
  const rawOrder = v['order'];
  const order =
    typeof rawOrder === 'number' && Number.isFinite(rawOrder) ? rawOrder : fallbackOrder;
  const target = parseTarget(v['target']);
  return {
    id,
    visible: v['visible'] !== false,
    order,
    size: v['size'] === 'lg' ? 'lg' : 'md',
    ...(target !== null ? { target } : {}),
  };
}

function parseCompare(v: unknown): ComparePair | null {
  if (!isRecord(v)) return null;
  const a = v['a'];
  const b = v['b'];
  if (typeof a !== 'string' || typeof b !== 'string' || a === '' || b === '') return null;
  return { a, b };
}

/**
 * Narrow an unknown stored blob into a well-formed `MonitorConfig`.
 * Anything malformed degrades gracefully: bad entries are dropped,
 * duplicate probe ids keep the first occurrence, missing fields get
 * defaults. Never throws.
 */
export function sanitizeMonitorConfig(
  raw: unknown,
  fallbackRangeHours: number = DEFAULT_RANGE_HOURS,
): MonitorConfig {
  if (!isRecord(raw)) {
    return { probes: [], rangeHours: fallbackRangeHours, compare: null };
  }
  const probes: MonitorProbeConfig[] = [];
  const seen = new Set<string>();
  const rawProbes = raw['probes'];
  if (Array.isArray(rawProbes)) {
    for (let i = 0; i < rawProbes.length; i++) {
      const entry = parseProbeEntry(rawProbes[i], i);
      if (entry === null || seen.has(entry.id)) continue;
      seen.add(entry.id);
      probes.push(entry);
    }
  }
  const rawRange = raw['rangeHours'];
  const rangeHours =
    typeof rawRange === 'number' && Number.isFinite(rawRange) && rawRange > 0
      ? rawRange
      : fallbackRangeHours;
  return { probes, rangeHours, compare: parseCompare(raw['compare']) };
}

// ── Merge with the live probe catalog ────────────────────────────────

function sortByOrder(probes: readonly MonitorProbeConfig[]): MonitorProbeConfig[] {
  // Stable sort by `order`; ties keep the stored array order (Array
  // .prototype.sort is spec-stable).
  return [...probes].sort((a, b) => a.order - b.order);
}

/**
 * Reconcile a sanitized stored config with the probes currently
 * reported by the Apex:
 *
 *   - stored entries are kept even when their probe is absent from the
 *     snapshot (Apex briefly offline must not lose settings);
 *   - newly-appeared probes are appended at the end, visible, size
 *     'md', with a preset target when one matches;
 *   - `order` is re-indexed to the canonical 0..n-1 sequence so
 *     move-up/down math stays trivial and duplicates self-heal.
 */
export function mergeConfigWithProbes(
  config: MonitorConfig,
  live: readonly LiveProbeInfo[],
): MonitorConfig {
  const ordered = sortByOrder(config.probes);
  const known = new Set(ordered.map((p) => p.id));
  for (const probe of live) {
    if (known.has(probe.name)) continue;
    known.add(probe.name);
    const preset = findPreset(probe.name, probe.probeType);
    ordered.push({
      id: probe.name,
      visible: true,
      order: ordered.length,
      size: 'md',
      ...(preset !== null ? { target: preset.target } : {}),
    });
  }
  return {
    ...config,
    probes: ordered.map((p, i) => (p.order === i ? p : { ...p, order: i })),
  };
}

/** Build a fresh config straight from the live catalog ("Reset to defaults"). */
export function defaultConfigFromProbes(
  live: readonly LiveProbeInfo[],
  rangeHours: number = DEFAULT_RANGE_HOURS,
): MonitorConfig {
  return mergeConfigWithProbes({ probes: [], rangeHours, compare: null }, live);
}

// ── Pure config mutations (page handlers call these + persist) ───────

/** Patch a probe's `visible` / `size` flags. Unknown id → unchanged config. */
export function updateProbe(
  config: MonitorConfig,
  id: string,
  patch: Partial<Pick<MonitorProbeConfig, 'visible' | 'size'>>,
): MonitorConfig {
  return {
    ...config,
    probes: config.probes.map((p) => (p.id === id ? { ...p, ...patch } : p)),
  };
}

/** Set (or clear, with `null`) a probe's target range. */
export function setProbeTarget(
  config: MonitorConfig,
  id: string,
  target: ProbeTarget | null,
): MonitorConfig {
  return {
    ...config,
    probes: config.probes.map((p) => {
      if (p.id !== id) return p;
      const { target: _dropped, ...rest } = p;
      return target === null ? rest : { ...rest, target };
    }),
  };
}

/**
 * Move a probe one slot up (-1) or down (+1) *among visible probes* —
 * hidden entries are skipped so customize-mode arrows match what the
 * grid shows. Out-of-bounds moves return the config unchanged.
 */
export function moveProbe(config: MonitorConfig, id: string, dir: -1 | 1): MonitorConfig {
  const visible = sortByOrder(config.probes.filter((p) => p.visible));
  const idx = visible.findIndex((p) => p.id === id);
  if (idx === -1) return config;
  const neighbor = visible[idx + dir];
  if (neighbor === undefined) return config;
  const self = visible[idx]!;
  return {
    ...config,
    probes: config.probes.map((p) => {
      if (p.id === self.id) return { ...p, order: neighbor.order };
      if (p.id === neighbor.id) return { ...p, order: self.order };
      return p;
    }),
  };
}
